import { X, AlertCircle, RotateCw } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useUserStore } from '@/stores/user-store'

// Show the reminder once the plan is within this many days of expiring.
const WARN_WITHIN_DAYS = 5

function todayKey(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Global reminder that plans are manual-renewal only. It surfaces as the
 * billing period approaches (or after it lapses), since we do not auto-charge
 * or store cards — the customer must pay again each cycle.
 */
export function RenewalBanner() {
  const navigate = useNavigate()
  const user = useUserStore((s) => s.user)
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('renewal_notice_dismissed') === todayKey(),
  )

  if (!user || user.plan_id === 'free' || !user.billing_cycle_end) return null

  const msLeft = new Date(user.billing_cycle_end).getTime() - Date.now()
  const daysLeft = Math.ceil(msLeft / 86_400_000)
  const isExpired = msLeft <= 0
  const isExpiringSoon = !isExpired && daysLeft <= WARN_WITHIN_DAYS

  if ((!isExpired && !isExpiringSoon) || dismissed) return null

  const periodLabel = user.plan?.billing_period_days === 365 ? 'year' : 'month'
  const planName = user.plan?.name?.replace(/ (Monthly|Annual)$/, '') ?? 'plan'
  const endDate = new Date(user.billing_cycle_end).toLocaleDateString()

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    localStorage.setItem('renewal_notice_dismissed', todayKey())
    setDismissed(true)
  }

  const tone = isExpired
    ? 'from-red-400/10 via-rose-300/15 to-red-400/10 border-red-400/15 hover:from-red-400/15 hover:via-rose-300/20 hover:to-red-400/15'
    : 'from-amber-300/10 via-yellow-200/15 to-amber-300/10 border-amber-300/15 hover:from-amber-300/15 hover:via-yellow-200/20 hover:to-amber-300/15'

  const Icon = isExpired ? AlertCircle : RotateCw
  const iconColor = isExpired ? 'text-red-300' : 'text-amber-300'
  const textColor = isExpired ? 'text-red-100' : 'text-amber-100'

  return (
    <div
      onClick={() => navigate('/billing')}
      className={`relative bg-gradient-to-r ${tone} border-b text-center py-3 px-12 sm:px-10 text-xs sm:text-sm cursor-pointer transition-all`}
    >
      <span className={`inline-flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 ${textColor}`}>
        <Icon className={`size-4 shrink-0 ${iconColor}`} />
        <span>
          {isExpired ? (
            <>
              Your <span className="font-semibold">{planName}</span> plan expired on {endDate}.{' '}
              <span className="underline underline-offset-2">Renew now</span> to keep generating.
            </>
          ) : (
            <>
              Your <span className="font-semibold">{planName}</span> plan ends in{' '}
              <span className="font-semibold">
                {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
              </span>{' '}
              ({endDate}). Plans don't auto-renew — pay again each {periodLabel} to keep your credits.{' '}
              <span className="underline underline-offset-2">Renew now</span>
            </>
          )}
        </span>
      </span>
      <button
        onClick={handleDismiss}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-all"
        aria-label="Dismiss reminder"
      >
        <X className="size-4" />
      </button>
    </div>
  )
}
